import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';

export interface UseUrlSearchOptions {
    paramName?: string;
    debounceDelay?: number;
    replace?: boolean;
    defaultValue?: string;
}

export const useDebouncedSearchParam = (options: UseUrlSearchOptions = {}) => {
    const { paramName = 'search', debounceDelay = 300, replace = true, defaultValue = '' } = options;

    const [searchParams, setSearchParams] = useSearchParams();
    const inputRef = useRef<any>(null);

    const searchValue = searchParams.get(paramName) || defaultValue;

    useEffect(() => {
        const searchField = inputRef.current?.input || inputRef.current;
        if (searchField instanceof HTMLInputElement) {
            searchField.value = searchValue;
        }
    }, [searchValue]);

    const updateUrlParams = useCallback(
        (value: string) => {
            const newParams = new URLSearchParams(searchParams);

            if (value && value !== defaultValue) {
                newParams.set(paramName, value);
            } else {
                newParams.delete(paramName);
            }

            setSearchParams(newParams, { replace });
        },
        [setSearchParams, searchParams, paramName, replace, defaultValue]
    );

    const debouncedUpdateSearch = useMemo(
        () => debounce(updateUrlParams, debounceDelay),
        [updateUrlParams, debounceDelay]
    );

    const handleSearch = useCallback(
        (value: string) => {
            debouncedUpdateSearch(value);
        },
        [debouncedUpdateSearch]
    );

    const clearSearch = useCallback(() => {
        const searchField = inputRef.current?.input || inputRef.current;
        if (searchField instanceof HTMLInputElement) {
            searchField.value = '';
        }
        updateUrlParams('');
    }, [updateUrlParams]);

    useEffect(() => {
        return () => {
            debouncedUpdateSearch.cancel();
        };
    }, [debouncedUpdateSearch]);

    return {
        searchValue,
        inputRef,
        handleSearch,
        clearSearch,
        searchParams,
        setSearchParams,
    };
};
